// src/features/editor/lib/draftHistory.ts
/**
 * Кольцевой буфер снимков черновика — чистая часть Undo/Redo (п.11).
 *
 * Глубина 10 — решение заказчика. Буфер именно кольцевой: одиннадцатая правка
 * вытесняет самую старую, а не растит стек без границ (снимок блока — целое
 * тело, включая элементы дизайна).
 */
export const HISTORY_DEPTH = 10

/**
 * Окно схлопывания серии правок, мс. Каждое поле редактора блоков пишет блок
 * ЦЕЛИКОМ на каждое `onChange`, то есть на каждое нажатие клавиши: без окна
 * «глубина 10» означала бы десять символов, и крупная правка (добавлен элемент
 * дизайна, переключён бейдж) вытеснялась бы из буфера следующей же набранной
 * строчкой — молча и именно тогда, когда отмена нужнее всего. Снимок делается
 * на ПЕРВОЙ правке серии: он и есть состояние «до того, как автор начал».
 */
export const DRAFT_COALESCE_MS = 450

export interface DraftHistory<T> {
  /** Снимки ДО правок, от самого старого к самому свежему. */
  readonly past: readonly T[]
  /** Снимки, отменённые Undo: вершина — ближайший шаг вперёд. */
  readonly future: readonly T[]
}

export const EMPTY_HISTORY: DraftHistory<never> = { past: [], future: [] }

export interface HistoryStep<T> {
  history: DraftHistory<T>
  value: T
}

const capped = <T>(items: readonly T[]): readonly T[] => items.slice(-HISTORY_DEPTH)

/**
 * Запомнить снимок ДО правки. Redo-хвост при этом обрезается: автор ушёл в
 * новую ветку, и «вперёд» вести уже некуда.
 */
export const recordDraft = <T>(history: DraftHistory<T>, before: T): DraftHistory<T> => ({
  past: capped([...history.past, before]),
  future: [],
})

/** Шаг назад: `null`, если отменять нечего. */
export const undoStep = <T>(history: DraftHistory<T>, current: T): HistoryStep<T> | null => {
  if (history.past.length === 0) return null
  return {
    value: history.past[history.past.length - 1],
    history: {
      past: history.past.slice(0, -1),
      future: capped([...history.future, current]),
    },
  }
}

/** Шаг вперёд: `null`, если возвращать нечего. */
export const redoStep = <T>(history: DraftHistory<T>, current: T): HistoryStep<T> | null => {
  if (history.future.length === 0) return null
  return {
    value: history.future[history.future.length - 1],
    history: {
      past: capped([...history.past, current]),
      future: history.future.slice(0, -1),
    },
  }
}
